import log from '@uwm/logger';

import {UwmRequestTimeoutError, UwmResponseError} from './errors';
import {sendMessageToParent} from './sendMessage';

// Request/response correlation over the one-way UWM postMessage transport. The
// consumer sends `{type, requestId, ...body}` outbound; the host replies with a
// command whose payload echoes the same `requestId`. The returned `handleResponse`
// must be registered in the consumer's command processor under each response
// command name.

type ResponsePayload = Record<string, unknown>;

interface PendingEntry {
    resolve: (payload: ResponsePayload) => void;
    reject: (error: Error) => void;
    timer: ReturnType<typeof setTimeout>;
}

interface CreateUwmRequestServiceOptions {
    // Outbound transport. Defaults to sendMessageToParent; injectable for tests.
    send?: (message: Record<string, unknown>) => void;
    defaultTimeoutMs?: number;
}

const DEFAULT_TIMEOUT_MS = 15_000;

// Each call returns an isolated instance with its own pending map, so separate
// modules never share correlation state.
export const createUwmRequestService = ({
    send = sendMessageToParent,
    defaultTimeoutMs = DEFAULT_TIMEOUT_MS,
}: CreateUwmRequestServiceOptions = {}) => {
    const pending = new Map<string, PendingEntry>();
    let fallbackCounter = 0;

    const generateRequestId = (requestType: string): string => {
        if (typeof globalThis.crypto?.randomUUID === 'function') {
            return globalThis.crypto.randomUUID();
        }

        fallbackCounter += 1;

        return `${requestType}-${fallbackCounter}`;
    };

    // Sends an outbound request and resolves with the host's correlated response
    // payload. Rejects with UwmRequestTimeoutError on timeout, or UwmResponseError
    // when the host replies with a `{error}` field. Transport-level only — callers
    // interpret application outcomes.
    const request = (
        requestType: string,
        body: Record<string, unknown> = {},
        timeoutMs: number = defaultTimeoutMs,
    ): Promise<ResponsePayload> => {
        const requestId = generateRequestId(requestType);

        return new Promise<ResponsePayload>((resolve, reject) => {
            const timer = setTimeout(() => {
                pending.delete(requestId);
                log.warn(`[UWM] request "${requestType}" (${requestId}) timed out after ${timeoutMs}ms`);
                reject(new UwmRequestTimeoutError(requestType));
            }, timeoutMs);

            pending.set(requestId, {resolve, reject, timer});
            send({type: requestType, requestId, ...body});
        });
    };

    // Inbound handler — register under each response command name. Keys on
    // payload.requestId, so one instance serves every round-trip.
    const handleResponse = (payload: ResponsePayload): void => {
        const requestId = payload.requestId as string | undefined;

        if (!requestId) {
            log.warn('[UWM] response without requestId — ignoring', payload);

            return;
        }

        const entry = pending.get(requestId);

        if (!entry) {
            log.warn(`[UWM] response for unknown/stale requestId ${requestId} — ignoring`);

            return;
        }

        clearTimeout(entry.timer);
        pending.delete(requestId);

        if (typeof payload.error === 'string') {
            entry.reject(new UwmResponseError(payload.error));

            return;
        }

        entry.resolve(payload);
    };

    return {request, handleResponse};
};
